import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, ClipboardList, Calendar } from 'lucide-react';

export default function FanManualStatsEntry() {
  const { gameId } = useParams();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [player, setPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState({
    pts: '', fga: '', fgm: '', fga_3pt: '', fgm_3pt: '',
    fta: '', ftm: '', oreb: '', dreb: '',
    ast: '', stl: '', blk: '', defl: '', to: '', pf: ''
  });
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGameData();
  }, [gameId]);

  const fetchGameData = async () => {
    try {
      const gameResponse = await api.get(`/fan/games/${gameId}`);
      const gameData = gameResponse.data;
      setGame(gameData);
      setPlayer(gameData.player);

      if (gameData.home_score) setHomeScore(gameData.home_score.toString());
      if (gameData.away_score) setAwayScore(gameData.away_score.toString());

      // Load existing box scores if editing
      try {
        const boxResponse = await api.get(`/fan/games/${gameId}/box-scores`);
        if (boxResponse.data?.length > 0) {
          const bs = boxResponse.data[0];
          setPlayerStats({
            pts: bs.pts?.toString() || '',
            fgm: bs.fgm?.toString() || '',
            fga: bs.fga?.toString() || '',
            fgm_3pt: bs.three_pm?.toString() || '',
            fga_3pt: bs.three_pa?.toString() || '',
            ftm: bs.ftm?.toString() || '',
            fta: bs.fta?.toString() || '',
            oreb: bs.oreb?.toString() || '',
            dreb: bs.dreb?.toString() || '',
            ast: bs.ast?.toString() || '',
            stl: bs.stl?.toString() || '',
            blk: bs.blk?.toString() || '',
            defl: bs.defl?.toString() || '',
            to: bs.to?.toString() || '',
            pf: bs.pf?.toString() || ''
          });
        }
      } catch {
        // No existing box scores
      }
    } catch (error) {
      toast.error('Failed to load game data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateStat = (stat, value) => {
    if (value !== '' && !/^\d*$/.test(value)) return;
    setPlayerStats(prev => ({ ...prev, [stat]: value }));
  };

  const calcPct = (made, att) => {
    const m = parseInt(made) || 0;
    const a = parseInt(att) || 0;
    if (a === 0) return '-';
    return Math.round((m / a) * 100) + '%';
  };

  const handleSave = async () => {
    if (!homeScore || !awayScore) {
      toast.error('Please enter the final score');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/fan/games/${gameId}/manual-stats`, {
        home_score: parseInt(homeScore),
        away_score: parseInt(awayScore),
        player_stats: {
          pts: parseInt(playerStats.pts) || 0,
          fgm: parseInt(playerStats.fgm) || 0,
          fga: parseInt(playerStats.fga) || 0,
          three_pm: parseInt(playerStats.fgm_3pt) || 0,
          three_pa: parseInt(playerStats.fga_3pt) || 0,
          ftm: parseInt(playerStats.ftm) || 0,
          fta: parseInt(playerStats.fta) || 0,
          oreb: parseInt(playerStats.oreb) || 0,
          dreb: parseInt(playerStats.dreb) || 0,
          ast: parseInt(playerStats.ast) || 0,
          stl: parseInt(playerStats.stl) || 0,
          blk: parseInt(playerStats.blk) || 0,
          defl: parseInt(playerStats.defl) || 0,
          to: parseInt(playerStats.to) || 0,
          pf: parseInt(playerStats.pf) || 0
        }
      });
      toast.success('Game summary saved!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save stats');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin h-12 w-12 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  return (
    <div
      className="h-full overflow-y-auto bg-gradient-to-b from-white to-slate-50 pb-app-nav"
      style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)' }}
    >
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-button" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-heading uppercase tracking-tight text-slate-900">Manual Stats Entry</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Game Info */}
        {game && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-slate-900">{game.is_home ? 'vs' : '@'} {game.opponent_name}</h2>
            <span className="flex items-center gap-1 text-sm text-slate-600 mt-2">
              <Calendar className="w-4 h-4" /> {formatDate(game.date_time)}
            </span>
          </div>
        )}

        {/* Manual Entry Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ClipboardList className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-900">Manual Game Summary</h3>
              <p className="text-sm text-amber-700 mt-1">You are entering a manual game summary for this matchup. Enter the final totals for each player below.</p>
            </div>
          </div>
        </div>

        {/* Final Score */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold font-heading uppercase tracking-tight text-slate-900 mb-4">Final Score</h3>
          <div className="grid grid-cols-2 gap-6 max-w-md">
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Your Team</Label>
              <Input
                type="number" min="0"
                value={game?.is_home ? homeScore : awayScore}
                onChange={(e) => game?.is_home ? setHomeScore(e.target.value) : setAwayScore(e.target.value)}
                data-testid="your-team-score-input"
                className="h-14 text-2xl font-bold text-center"
                placeholder="0"
              />
            </div>
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Opponent</Label>
              <Input
                type="number" min="0"
                value={game?.is_home ? awayScore : homeScore}
                onChange={(e) => game?.is_home ? setAwayScore(e.target.value) : setHomeScore(e.target.value)}
                data-testid="opponent-score-input"
                className="h-14 text-2xl font-bold text-center"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Player Stats */}
        {player && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold font-heading uppercase tracking-tight text-slate-900">Player Stats</h3>
              <p className="text-sm text-slate-500 mt-1">Enter final totals for each player</p>
            </div>

            <div className="p-6" data-testid="player-stats-section">
              {/* Player Header */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center font-bold text-white text-lg flex-shrink-0">
                  {player.jersey_number || '-'}
                </div>
                <h4 className="font-bold text-slate-900" data-testid="player-stats-name">{player.name}</h4>

                <div className="flex items-center gap-3 text-sm ml-auto">
                  <span className="px-2 py-1 bg-slate-100 rounded font-medium">FG: <span className="text-slate-700">{calcPct(playerStats.fgm, playerStats.fga)}</span></span>
                  <span className="px-2 py-1 bg-slate-100 rounded font-medium">3PT: <span className="text-slate-700">{calcPct(playerStats.fgm_3pt, playerStats.fga_3pt)}</span></span>
                  <span className="px-2 py-1 bg-slate-100 rounded font-medium">FT: <span className="text-slate-700">{calcPct(playerStats.ftm, playerStats.fta)}</span></span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-700">Points</Label>
                  <Input
                    type="text" inputMode="numeric"
                    value={playerStats.pts}
                    onChange={(e) => updateStat('pts', e.target.value)}
                    data-testid="player-points-input"
                    className="h-12 w-20 text-center text-xl font-bold"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Row 1: Shooting */}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-4 mb-4">
                <StatInput label="FG Made" value={playerStats.fgm} onChange={(v) => updateStat('fgm', v)} testId="stat-fgm" />
                <StatInput label="FG Att" value={playerStats.fga} onChange={(v) => updateStat('fga', v)} testId="stat-fga" />
                <StatInput label="3PT Made" value={playerStats.fgm_3pt} onChange={(v) => updateStat('fgm_3pt', v)} testId="stat-3pm" />
                <StatInput label="3PT Att" value={playerStats.fga_3pt} onChange={(v) => updateStat('fga_3pt', v)} testId="stat-3pa" />
                <StatInput label="FT Made" value={playerStats.ftm} onChange={(v) => updateStat('ftm', v)} testId="stat-ftm" />
                <StatInput label="FT Att" value={playerStats.fta} onChange={(v) => updateStat('fta', v)} testId="stat-fta" />
                <StatInput label="Off Reb" value={playerStats.oreb} onChange={(v) => updateStat('oreb', v)} testId="stat-oreb" />
              </div>

              {/* Row 2: Other stats */}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-4">
                <StatInput label="Def Reb" value={playerStats.dreb} onChange={(v) => updateStat('dreb', v)} testId="stat-dreb" />
                <StatInput label="Assists" value={playerStats.ast} onChange={(v) => updateStat('ast', v)} testId="stat-ast" />
                <StatInput label="Steals" value={playerStats.stl} onChange={(v) => updateStat('stl', v)} testId="stat-stl" />
                <StatInput label="Blocks" value={playerStats.blk} onChange={(v) => updateStat('blk', v)} testId="stat-blk" />
                <StatInput label="Deflections" value={playerStats.defl} onChange={(v) => updateStat('defl', v)} testId="stat-defl" />
                <StatInput label="Turnovers" value={playerStats.to} onChange={(v) => updateStat('to', v)} testId="stat-to" />
                <StatInput label="Fouls" value={playerStats.pf} onChange={(v) => updateStat('pf', v)} testId="stat-pf" />
              </div>
            </div>
          </div>
        )}

        {/* Save */}
        <div className="flex justify-end gap-4 pb-8">
          <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} className="h-12 px-6">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-manual-stats-button"
            className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold uppercase tracking-wide"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving...' : 'Save Game Summary'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatInput({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">{label}</Label>
      <Input
        type="text" inputMode="numeric"
        value={value}
        onChange={(e) => {
          if (e.target.value !== '' && !/^\d*$/.test(e.target.value)) return;
          onChange(e.target.value);
        }}
        data-testid={testId}
        className="h-10 text-center"
        placeholder="0"
      />
    </div>
  );
}
